export type SupportedMeetingLanguage = 'en' | 'tr' | 'es' | 'el' | 'de';

const MEETING_BASE_URL = 'https://meeting.sagel.app';

interface MeetingLinkParams {
  companyName: string;
  appointmentDate: string;
  appointmentTime: string;
  timezone: string;
  language: SupportedMeetingLanguage;
}

export function buildSagelMeetingUrl(params: MeetingLinkParams): string {
  const appointmentDate = normalizeDate(params.appointmentDate);
  const appointmentTime = normalizeTime(params.appointmentTime);
  const timezone = normalizeTimezone(params.timezone);
  const language = normalizeLanguage(params.language);
  const companySlug = slugifySegment(params.companyName) || 'sagel';

  if (!appointmentDate || !appointmentTime || !timezone) {
    return '';
  }

  return `${MEETING_BASE_URL}/${companySlug}/appointment-${appointmentDate}_${appointmentTime}-${timezone}-${language}`;
}

export function resolveMeetingUrl(...candidates: Array<string | null | undefined>): string {
  let firstNonEmpty = '';

  for (const candidate of candidates) {
    const value = String(candidate ?? '').trim();
    if (!value) {
      continue;
    }

    if (!firstNonEmpty) {
      firstNonEmpty = value;
    }

    if (/^https?:\/\//i.test(value)) {
      return value;
    }
  }

  return firstNonEmpty;
}

function normalizeDate(value: string): string {
  const raw = String(value ?? '').trim();
  const ymdMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (ymdMatch) {
    return ymdMatch[1];
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return [
    parsed.getFullYear(),
    String(parsed.getMonth() + 1).padStart(2, '0'),
    String(parsed.getDate()).padStart(2, '0'),
  ].join('-');
}

function normalizeTime(value: string): string {
  const raw = String(value ?? '').trim();
  const match = raw.match(/^(\d{2}):(\d{2})/);
  if (!match) {
    return '';
  }

  return `${match[1]}-${match[2]}`;
}

function normalizeTimezone(value: string): string {
  return String(value ?? '')
    .trim()
    .replace(/\//g, '_')
    .replace(/\s+/g, '_');
}

function normalizeLanguage(value: string): SupportedMeetingLanguage {
  const supported: SupportedMeetingLanguage[] = ['en', 'tr', 'es', 'el', 'de'];
  return supported.includes(value as SupportedMeetingLanguage)
    ? (value as SupportedMeetingLanguage)
    : 'en';
}

function slugifySegment(value: string): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
